<template>
  <el-dialog
      :title="t('specialPurchaseOrder')"
      v-model="dialogVisible"
      width="450"
      :before-close="handleClose"
  >
    <el-form
        v-if="!readonly"
        ref="ruleFormRef"
        :model="ruleForm"
        :rules="rules"
        label-width="auto"
        :size="formSize"
        status-icon
        class="!block mt-2"
    >
      <el-form-item :label="t('vendor')" prop="vendor" required>
        <el-select filterable clearable v-model="ruleForm.vendor"
                   :placeholder="t('selectVendor')" @change="onVendorChange">
          <el-option v-for="vendor in vendors" :key="vendor.id"
                     :label="vendor.name" :value="vendor.id"/>
        </el-select>
      </el-form-item>

      <el-form-item :label="t('manualNumber')" prop="manualNumber">
        <el-input v-model="ruleForm.manualNumber"/>
      </el-form-item>


      <el-form-item :label="t('notes')" prop="note">
        <el-input v-model="ruleForm.notes"/>
      </el-form-item>


      <el-form-item :label="t('dueDate2')">
        <el-form-item prop="dueDate">
          <el-date-picker
              v-model="ruleForm.dueDate"
              type="date"
              :placeholder="t('pickADate')"
              style="width: 100%"
          />
        </el-form-item>
      </el-form-item>

    </el-form>
    <div v-if="readonly">
      <div class="flex flex-col-reverse gap-4">
        <div class="flex items-center justify-between">
          <div class="flex gap-4 items-center">
            <span
                class="flex h-8 items-center  rounded-2xl px-3 py-2 text-sm bg-[#FFFCC5]">{{
                t('purchased')
              }}</span>
            <button @click="() => {
              router.push($Route.INVENTORY_PURCHASE_ORDER.replace(':id', orderItem?.purchaseOrder?.id))
            }">
            #{{ orderItem?.purchaseOrder?.externalID}}
          </button>
          </div>
          <span>{{
              formatPostgresDate(orderItem?.purchaseOrder?.createdAt)
            }}</span>
        </div>
        <div v-for="(ir, index) in orderItem.purchaseOrder.inventoryReceivings"
             :key="ir.id" class="flex items-center justify-between">
          <span
              class="flex h-8 items-center  rounded-2xl px-3 py-2 text-sm"
              :class="{'bg-[#C5DFFF]': irStatuses[index] == 'partial', 'bg-[#D0FFC5]': irStatuses[index] == 'full'}">{{
              t(irStatuses[index])
            }}</span>
          <span>{{ formatPostgresDate(ir?.createdAt) }}</span>
        </div>
      </div>
    </div>
    <template #footer>
      <div v-if="!readonly" class="dialog-footer">
        <el-button @click="clear()">{{ t('clear2') }}</el-button>
        <el-button @click="dialogVisible = false">{{ t('close') }}</el-button>
        <el-button type="primary" @click="submitForm(ruleFormRef)">
          {{ t('confirm') }}
        </el-button>
      </div>
    </template>
  </el-dialog>
  <button
      v-if="(readonly && orderItem.specialOrderItemInfo) || orderItem.specialOrderItemInfo || isGroup"
      class="text-black hover:text-blue-500 font-bold py-1 px-1 rounded"
      :class="{'text-blue-500': orderItem.specialOrderItemInfo && !isGroup}"
      :style="{'color': orderItem.specialOrderItemInfo && !isGroup? orderItem.specialOrderItemInfo.color: ''}"
      type="button"
      @click="load"
  >
    <font-awesome-icon class=" cursor-pointer outline-none" icon="shopping-cart"
    />
  </button>
</template>

<script setup lang="ts">
import {computed, nextTick, onMounted, ref} from 'vue'
import type {ComponentSize, FormInstance, FormRules} from 'element-plus'
import {ElLoading, ElMessage} from "element-plus";

import {useHTTP} from "@/plugins/http";
import {useI18n} from "vue-i18n";
import {useContext} from "@/plugins/context";
import Vendor from "@/models/vendor/Vendor";
import {formatPostgresDate} from "../../utils/date";
import {useRouter} from "vue-router";
import { v4 as uuidv4 } from 'uuid'
import randomcolor from 'randomcolor'

const props = defineProps(['orderItem', 'readonly', 'isGroup'])
const emit = defineEmits(['submit']);

const http = useHTTP();
const router = useRouter();
const ctx = useContext();

const messages = {
  en: {
    pleaseWait: 'Please wait...',
    close: 'Close',
    specialPurchaseOrder: 'Special Purchase Order',
    confirm: 'Confirm',
    notes: 'Notes',
    manualNumber: 'Manual No',
    selectVendor: 'Select Vendor',
    purchased: 'Purchased',
    partial: 'Partial Received',
    full: 'Received',
    dueDate2: 'Due Date',
    pickADate: 'Pick a Date',
    clear2: 'Clear'
  },
  ar: {
    pleaseWait: 'يرجى الانتظار...',
    close: 'اغلاق',
    specialPurchaseOrder: 'طلب شراء مميز',
    confirm: 'تأكيد',
    notes: 'الملاحظات',
    manualNumber: 'الرقم الخاص',
    selectVendor: 'اختر المورّد',
    purchased: 'طلب شراء',
    partial: 'مستلم جزئيا',
    full: 'مستلم',
    dueDate2: 'التاريخ المتوقع',
    pickADate: 'اختر تاريخ',
    clear2: 'تفريغ'
  }
}
const {t} = useI18n({messages, useScope: 'global'})

const dialogVisible = ref(false)
const isLoading = ref(false)

const vendors = ref<Vendor>([])

onMounted(() => {
  if (props.orderItem.specialOrderItemInfo) {
    Object.assign(ruleForm.value, props.orderItem.specialOrderItemInfo)
  }
})

interface RuleForm {
  vendor?: string
  manualNumber?: string
  notes?: string
  dueDate?: string
  groupID: string
  color: string
}

const formSize = ref<ComponentSize>('default')
const ruleFormRef = ref<FormInstance>()
const ruleForm = ref<RuleForm>({
  vendor: undefined,
  manualNumber: undefined,
  notes: '',
  dueDate: '',
  groupID: '',
  color: ''
})


const rules = reactive<FormRules<RuleForm>>({
  vendor: [
    {
      required: true,
      message: t('validation.required'),
      trigger: 'blur'
    },
  ],
  manualNumber: [
    {
      required: false,
      trigger: 'change',
    },
  ],
  notes: [
    {
      required: false,
      trigger: 'change',
    },
  ],
  dueDate: [
    {
      type: 'date',
      required: false,
      trigger: 'change',
    },
  ],
})


const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return
  await formEl.validate((valid, fields) => {
    if (valid) {
      if (props.isGroup && ruleForm.value.groupID == "") {
        ruleForm.value.groupID = uuidv4();
        ruleForm.value.color = randomcolor({
          luminosity: 'dark',
        });
      }
      if (ruleForm.value.dueDate == '') {
        ruleForm.value.dueDate = undefined;
      }
      if (!props.isGroup && ruleForm.value.groupID && props.orderItem.specialOrderItemInfo && (ruleForm.value.vendor != props.orderItem.specialOrderItemInfo.vendor || ruleForm.value.dueDate != props.orderItem.specialOrderItemInfo.dueDate)){
        ruleForm.value.groupID = uuidv4();
        ruleForm.value.color = randomcolor({
          luminosity: 'dark',
        });
      }
      emit("submit", ruleForm.value)
      console.log(ruleForm.value)
      dialogVisible.value = false;
    } else {
      console.log('error submit!', fields)
    }
  })
}

const resetForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return
  formEl.resetFields()
  ruleForm.value = {
    vendor: undefined,
    notes: '',
    dueDate: '',
    groupID: '',
    color: '',
  }
}

function clear() {
  ruleForm.value = {
    vendor: undefined,
    manualNumber: undefined,
    notes: '',
    dueDate: '',
    groupID: '',
    color: '',
  }
  emit("submit", undefined);
  dialogVisible.value = false;
}


const handleClose = (done: () => void) => {
  if (!isLoading.value) {
    done();
  }
}

function onVendorChange(event: any) {
  if (!event) {
    emit("submit", undefined)
    resetForm(ruleFormRef.value)
  }
}

async function load() {
  Object.assign(ruleForm.value, props.orderItem.specialOrderItemInfo)
  dialogVisible.value = true
  await nextTick()
  isLoading.value = true;
  const loading = ElLoading.service({
    lock: true,
    target: document.querySelector(".el-dialog"),
    text: t('pleaseWait'),
  })

  try {
    const resp = await http.get<Vendor[]>(`${Vendor.ENDPOINT}`)
    vendors.value = resp.map(v => Vendor.from(v));
  } catch (error) {
    ElMessage({
      message: error.body,
      type: 'error'
    })
  }
  loading.close()
  isLoading.value = false;
}

const irStatuses = computed(() => {
  let remaining = props.orderItem.purchaseOrder.orderItems.reduce(
      (sum, poi) => sum + (poi.quantity || 0),
      0
  );

  return props.orderItem.purchaseOrder.inventoryReceivings.map((item) => {
    const irQuantity = item.inventoryReceivingItems.reduce(
        (sum, iri) => sum + (iri.quantity || 0),
        0
    );

    if (remaining > 0) {
      if (irQuantity < remaining) {
        remaining -= irQuantity;
        return 'partial';
      } else {
        remaining = 0;
        return 'full';
      }
    }

    return 'full'; // Default case if no remaining
  });
});
</script>

<style scoped>

</style>
